import React, {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {debounce} from "lodash";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {SelectField} from "./FilterComponentStyles";
import {CourierDetailBox} from "../../shiftManagement/Components/ShiftEntryModal/CourierDetailBox/CourierDetailBox";
import {
    arrayAlphabeticalSort,
    normalizeStringSearchFilter,
    toTurkishLowerCase
} from "../../../utils/helpers";
import {Spin} from "antd";

const mapCourierOptions = initialOptions => {
    return (
        initialOptions?.map(courier => ({
            _id: courier?._id?.toString(),
            fullname: courier?.fullname,
            tckn: courier?.tckn,
            cellPhone: courier?.cellPhone
        })) || []
    );
};

const CourierFilter = ({
    hideTitle = false,
    value,
    onChange,
    selectMarginBottom = "20px",
    initialOptions = [],
    activity
}) => {
    const [courierList, setCourierList] = useState(mapCourierOptions(initialOptions));
    const {t} = useTranslation();
    const stores = useStore();
    const [searchText, setSearchText] = useState("");
    const debounceTimeout = 300;

    const [loading, , ,] = useFetch({
        fetchFn: stores.courierStore.getCourierList.bind(null, {
            limit: 10,
            skip: 0,
            statuses: [],
            types: [],
            searchText,
            activity
        }),
        dependencies: [searchText],
        condition: !stores.meStore.checkRole("isCompanyUser") && searchText,
        initialCall: false,
        callbackFunc: data => {
            const newCourierList = [...data?.rows];
            courierList.forEach(courier => {
                if (!newCourierList.find(c => c?._id?.toString() === courier?._id?.toString())) {
                    newCourierList.push(courier);
                }
            });
            setCourierList(mapCourierOptions(newCourierList));
        }
    });

    const onSearchUser = useMemo(() => {
        const loadOptions = value => {
            setSearchText(normalizeStringSearchFilter(toTurkishLowerCase(value?.trim() || "")));
        };
        return debounce(loadOptions, debounceTimeout);
    }, [debounceTimeout]);

    return (
        <>
            {!hideTitle && <h5>{t("fields.courier", {ns: "common"})}</h5>}
            <SelectField
                allowClear
                showSearch
                placeholder={""}
                value={value}
                mb={selectMarginBottom}
                onSearch={onSearchUser}
                onChange={(value, key) => {
                    onChange(value, key?.key);
                }}
                loading={loading}
                isLoading={loading}
                filterOption={(input, option) => {
                    const normalizedInput = normalizeStringSearchFilter(
                        toTurkishLowerCase(input || "")
                    );
                    return [
                        option?.children,
                        option?.value,
                        option?.props["data-tckn"],
                        option?.props["data-cellPhone"]
                    ].some(
                        field =>
                            typeof field === "string" &&
                            normalizeStringSearchFilter(toTurkishLowerCase(field)).includes(
                                normalizedInput
                            )
                    );
                }}
                notFoundContent={
                    loading ? (
                        <Spin />
                    ) : searchText ? (
                        "Kurye bulunamadı"
                    ) : (
                        "İsim, tckn, telefon giriniz"
                    )
                }
            >
                {loading ? (
                    <Spin />
                ) : (
                    courierList &&
                    courierList?.length > 0 &&
                    arrayAlphabeticalSort([...courierList], "fullname").map(courier => (
                        <SelectField.Option
                            key={courier._id}
                            value={`${courier._id}`}
                            data-tckn={courier.tckn}
                            data-cellPhone={courier.cellPhone}
                            data-testid="courierSelect_option"
                        >
                            {courier.fullname}
                        </SelectField.Option>
                    ))
                )}
            </SelectField>
        </>
    );
};

export default CourierFilter;
